import type { PogAdapterOptions } from "@CommonUtils/cdtStudio/adapters/pogToStudioItems";
import type { AttributeCandidate, InferenceOptions } from "../domain/spineInference";
import { terminalLastValuesOf, type StudioProfile, validateStudioProfile } from "./studioProfile";

export interface CompiledStudioItemMaster {
  adapterOptions: PogAdapterOptions;
  pogAdapterOptions: PogAdapterOptions;
  preferredOrder: readonly string[];
  inferenceOptions: InferenceOptions;
  aestheticsRules: StudioProfile["aestheticsRules"];
  hierarchyMoveRules: StudioProfile["hierarchyMoveRules"];
  verticalBlockingOptions: StudioProfile["verticalBlocking"];
  semanticRoles: StudioProfile["semanticRoles"];
}

/** Profile-only compile: live product-attributes are the item values. */
export const compileStudioProfile = (profile: StudioProfile): CompiledStudioItemMaster => {
  validateStudioProfile(profile);
  const overlayCandidates: AttributeCandidate[] = profile.attributes.map(({ key, label }) => ({
    attr: key,
    label,
  }));
  const normalization = profile.quantityNormalization;
  const adapterOptions: PogAdapterOptions = {
    itemIdentifier: profile.itemIdentifier,
    attributeOverlay: {},
    nameOverlay: {},
    overlayCandidates,
    normalization: {
      placeholderSpellings: profile.missingValues,
      quantityAttribute: normalization.enabled ? profile.semanticRoles.quantity : false,
      nameBasedUnitInference: normalization.inferFromName,
      liquidNameTerms: normalization.liquidNameTerms,
      unitPreference: normalization.unitPreference,
      unitAliases: normalization.unitAliases,
      zeroIsMissing: normalization.zeroIsMissing,
      liquidOuncesAreFluid: normalization.liquidOuncesAreFluid,
      minimumFamilyEvidence: normalization.minimumFamilyEvidence,
      reconciliationRange: normalization.reconciliationRange,
      reconciliationBlockAttributes: normalization.reconciliationBlockAttributes,
    },
  };
  const preferredOrder = profile.attributes.map((attribute) => attribute.key);
  const inferenceOptions: InferenceOptions = {
    preferredOrder,
    verticalBlockShare: profile.verticalBlocking.blockShare,
    fragmentJoinPct: profile.verticalBlocking.fragmentJoinPct,
    // Read against the session's plan (`withPlanFragmentJoin`): its unit is the plan's.
    ...(profile.verticalBlocking.fragmentJoinPct === undefined && {
      legacyFragmentJoin: profile.verticalBlocking.fragmentJoinCm,
    }),
    firstPreferredValueMatch: profile.preferredValueRules.first,
    lastPreferredValueMatch: profile.preferredValueRules.last,
    firstPreferredValues: Object.fromEntries(
      profile.firstValueRules.map((rule) => [rule.attribute, rule.values]),
    ),
    lastPreferredValues: Object.fromEntries(
      profile.lastValueRules.map((rule) => [rule.attribute, rule.values]),
    ),
  };
  return {
    adapterOptions,
    pogAdapterOptions: adapterOptions,
    preferredOrder,
    inferenceOptions,
    aestheticsRules: {
      ...profile.aestheticsRules,
      brandAttr: profile.semanticRoles.brand,
      tierAttr: profile.semanticRoles.tier,
      sizeAttr: profile.semanticRoles.quantity,
      flavourAttr: profile.semanticRoles.terminalVariant,
      verticalAttrs: profile.semanticRoles.vertical,
      lastInBlock: terminalLastValuesOf(profile),
    },
    hierarchyMoveRules: profile.hierarchyMoveRules,
    verticalBlockingOptions: profile.verticalBlocking,
    semanticRoles: profile.semanticRoles,
  };
};
